import React, { useEffect } from 'react';
import { InfoCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons';
import './AlertBanner.css';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

interface AlertBannerProps {
  /** The message to display in the alert */
  message: React.ReactNode;
  /** The type of alert - determines color and title. Default: 'info' */
  type?: AlertType;
  /** Callback function when the alert is closed */
  onClose: () => void;
  /** Whether to automatically close the alert. Default: true */
  autoClose?: boolean;
  /** Time in milliseconds before auto-closing. Default: 5000 (5 seconds) */
  autoCloseDelay?: number;
  /** Optional custom className for additional styling */
  className?: string;
}

/**
 * AlertBanner - A reusable full-width alert banner component
 *
 * @example
 * ```tsx
 * const [showAlert, setShowAlert] = useState(true);
 *
 * return (
 *   <>
 *     {showAlert && (
 *       <AlertBanner
 *         message="Profile updated successfully"
 *         type="success"
 *         onClose={() => setShowAlert(false)}
 *       />
 *     )}
 *   </>
 * );
 * ```
 */
const AlertBanner: React.FC<AlertBannerProps> = ({
  message,
  type = 'info',
  onClose,
  autoClose = false,
  autoCloseDelay = 10000,
  className,
}) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [autoClose, autoCloseDelay, onClose]);

  const getAlertTitle = () => {
    switch (type) {
      case 'success':
        return 'SUCCESS';
      case 'error':
        return 'ERROR';
      case 'warning':
        return 'WARNING';
      case 'info':
      default:
        return 'INFORMATIONAL';
    }
  };

  const getAlertIcon = () => {
    switch (type) {
      case 'success':
        return <InfoCircleFill size={24} className="alert-icon-svg" />;
      case 'error':
        return <ExclamationTriangleFill size={24} className="alert-icon-svg" />;
      case 'warning':
        return <ExclamationTriangleFill size={24} className="alert-icon-svg" />;
      case 'info':
      default:
        return <InfoCircleFill size={24} className="alert-icon-svg" />;
    }
  };

  const alertClass = `alert-banner ${type}-alert${className ? ` ${className}` : ''}`;

  return (
    <div className={alertClass}>
      <div className="alert-icon-box">
        {getAlertIcon()}
      </div>
      <div className="alert-content">
        <div className="alert-text">
          <div className="alert-title">{getAlertTitle()}</div>
          <div className="alert-message">{message}</div>
        </div>
      </div>
      <button className="alert-close" onClick={onClose} aria-label="Close alert">
        ×
      </button>
    </div>
  );
};

export default AlertBanner;
